import { format, subDays } from "date-fns";

/** Friendly names and brand-like colours for the channels the backend reports. */
export const CHANNEL_META = {
  facebook: { label: "Facebook", color: "#1877F2" },
  instagram: { label: "Instagram", color: "#E1306C" },
  tiktok: { label: "TikTok", color: "#111111" },
  youtube: { label: "YouTube", color: "#FF0000" },
  whatsapp: { label: "WhatsApp", color: "#25D366" },
  google_ads: { label: "Google Ads", color: "#4285F4" },
  google_organic: { label: "Google (organic)", color: "#34A853" },
  email: { label: "Email", color: "#F59E0B" },
  referral: { label: "Referral", color: "#8B5CF6" },
  direct: { label: "Direct", color: "#64748B" },
  other: { label: "Other", color: "#94A3B8" },
  unattributed: { label: "Unattributed", color: "#CBD5E1" },
};

export const PLATFORM_META = {
  web: { label: "Web", color: "#7c3aed" },
  ios: { label: "iOS", color: "#111827" },
  android: { label: "Android", color: "#3DDC84" },
  unknown: { label: "Unknown", color: "#CBD5E1" },
};

export const FUNNEL_LABELS = {
  leads: "Leads",
  signups: "Sign-ups",
  trials_started: "Trials started",
  paid_conversions: "Paid",
};

export const RANGE_PRESETS = [
  { value: "7d", label: "7 days", days: 7 },
  { value: "30d", label: "30 days", days: 30 },
  { value: "90d", label: "90 days", days: 90 },
  { value: "custom", label: "Custom" },
];

const titleCase = (s) =>
  String(s || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export function channelLabel(channel) {
  return CHANNEL_META[channel]?.label || titleCase(channel) || "Unknown";
}

export function channelColor(channel) {
  return CHANNEL_META[channel]?.color || "#94A3B8";
}

export function platformLabel(platform) {
  return PLATFORM_META[platform]?.label || titleCase(platform) || "Unknown";
}

/** Lead source ids from the backend (email_captured, checkout_abandoned…) as readable text. */
export function leadSourceLabel(source) {
  const known = {
    email_captured: "Email captured (form)",
    checkout_abandoned: "Abandoned checkout",
    checkout_purchase: "Checkout purchase",
    website_contact_form: "Website contact form",
    unknown: "Unknown",
  };
  return known[source] || titleCase(source) || "Unknown";
}

const gbp = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function formatGBP(value) {
  const n = Number(value);
  return gbp.format(Number.isFinite(n) ? n : 0);
}

export function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0%";
  return `${Math.round(n * 10) / 10}%`;
}

export function formatCount(value) {
  const n = Number(value);
  return (Number.isFinite(n) ? n : 0).toLocaleString("en-GB");
}

/** from/to (yyyy-MM-dd) for a preset, or the custom dates. */
export function rangeToDates(preset, custom = {}, today = new Date()) {
  const found = RANGE_PRESETS.find((p) => p.value === preset);
  if (found?.days) {
    return {
      from: format(subDays(today, found.days - 1), "yyyy-MM-dd"),
      to: format(today, "yyyy-MM-dd"),
    };
  }
  return { from: custom.from || "", to: custom.to || "" };
}

/** Each funnel step with its % of the previous step (null for the first). */
export function funnelWithRates(funnel = []) {
  return funnel.map((s, i) => {
    const prev = i > 0 ? Number(funnel[i - 1]?.count) || 0 : null;
    const count = Number(s?.count) || 0;
    return {
      ...s,
      count,
      label: FUNNEL_LABELS[s?.step] || titleCase(s?.step),
      stepRate: prev === null ? null : prev > 0 ? Math.round((count / prev) * 1000) / 10 : 0,
    };
  });
}

/** True when the period has no marketing activity at all (tracking just started). */
export function isEmptyReport(data) {
  const s = data?.summary || {};
  return !(
    Number(s.leads) ||
    Number(s.signups) ||
    Number(s.trials_started) ||
    Number(s.paid_conversions) ||
    Number(s.revenue_gbp)
  );
}

/** Stable sort for the channels table. */
export function sortRows(rows = [], key, direction = "desc") {
  const factor = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = key === "channel" ? channelLabel(a.channel) : Number(a[key]) || 0;
    const bv = key === "channel" ? channelLabel(b.channel) : Number(b[key]) || 0;
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
}
